/**
 * How dotenv reads a `.env` line, and the names a variable can have, as the
 * save's validation (`environment-validation.ts`), secret masking
 * (`secret-variables.ts`) and the browser, such as the suggestion card
 * (`environment-suggestion.ts`), need them. No imports: the browser has
 * neither dotenv nor Node's modules.
 */

/**
 * A name the environment takes: letters, digits and underscores, not starting
 * with a digit, excluding exact lowercase `__proto__`.
 */
export const VARIABLE_NAME_PATTERN = /^(?!__proto__$)[A-Za-z_]\w*$/;

/** How dotenv starts an assignment: an optional `export`, a name, then `=` or `: `. */
export const ASSIGNMENT_START_PATTERN = /^\s*(?:export\s+)?([\w.-]+)(?:\s*=|:\s)/;

/** The quotes a dotenv value can open, and that value may then span lines until it closes. */
export const VALUE_QUOTES: ReadonlySet<string> = new Set(['"', "'", '`']);

/** Where `text` holds an unescaped `quote` at or after `from`, or -1. */
export function quoteIndex(text: string, quote: string, from = 0) {
  for (let index = from; index < text.length; index += 1) {
    if (text[index] === '\\') {
      index += 1;
    } else if (text[index] === quote) {
      return index;
    }
  }
  return -1;
}

const DOTENV_LINE_PATTERN =
  /(?:^|^)\s*(?:export\s+)?([\w.-]+)(?:\s*=\s*?|:\s+?)(\s*'(?:\\'|[^'])*'|\s*"(?:\\"|[^"])*"|\s*`(?:\\`|[^`])*`|[^#\r\n]+)?\s*(?:#.*)?(?:$|$)/gm;

/**
 * The same pattern with `d`, so each match has where its value ends. Built
 * from the source: the es2018 TypeScript target refuses `d` in a literal.
 */
const DOTENV_LINE_INDICES_PATTERN = new RegExp(DOTENV_LINE_PATTERN.source, 'dgm');

export type DotenvVariable = { name: string; value: string };

/**
 * A matched value as dotenv reads it: trimmed and unquoted, with `\n` and
 * `\r` escapes expanded in double quotes.
 */
function dotenvValue(matched: string | undefined): string {
  const value = (matched ?? '').trim();
  const unquoted = value.replace(/^(['"`])([\s\S]*)\1$/gm, '$2');
  return value[0] === '"' ? unquoted.replace(/\\n/g, '\n').replace(/\\r/g, '\r') : unquoted;
}

/** dotenv 17.4.2's `parse`, copied because the browser has no dotenv. */
export function parseDotenvText(text: string): DotenvVariable[] {
  // Like dotenv's object, a Map keeps a name set again in its first place; unlike it, it keeps `__proto__`.
  const variables = new Map<string, string>();
  for (const match of text.replace(/\r\n?/gm, '\n').matchAll(DOTENV_LINE_PATTERN)) {
    variables.set(match[1]!, dotenvValue(match[2]));
  }
  return Array.from(variables, ([name, value]) => ({ name, value }));
}

/**
 * The text with each assignment dotenv reads, a name set again included,
 * replaced by the line `replacement` answers for it, or kept when it answers
 * null. A replacement takes the lines from the name to the end of the value,
 * an inline comment included. Every other character stays as written.
 */
export function replaceDotenvAssignments(
  text: string,
  replacement: (variable: DotenvVariable) => string | null,
): string {
  // dotenv reads each `\r\n` or lone `\r` as `\n`. `starts[i]` is where `unix[i]` is in `text`.
  const unix = text.replace(/\r\n?/gm, '\n');
  const starts = Array.from(text.matchAll(/\r\n|[\s\S]/g), (match) => match.index);
  starts.push(text.length);
  let replaced = '';
  let copied = 0;
  for (const match of unix.matchAll(DOTENV_LINE_INDICES_PATTERN)) {
    const line = replacement({ name: match[1]!, value: dotenvValue(match[2]) });
    if (line === null) {
      continue;
    }
    // A match also takes in blank lines before it, and blank and comment lines after it. Those stay.
    const first = match.index + match[0].length - match[0].trimStart().length;
    const lineEnd = unix.indexOf('\n', match.indices![2]?.[1] ?? first);
    const start = starts[unix.lastIndexOf('\n', first) + 1]!;
    const end = starts[lineEnd === -1 ? unix.length : lineEnd]!;
    replaced += text.slice(copied, start) + line;
    copied = end;
  }
  return replaced + text.slice(copied);
}
